// src/routes/_layout/writings.tsx
import {
  Box,
  Container,
  Flex,
  Grid,
  Heading,
  HStack,
  Text,
  VStack,
  Table,
  Button,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { FiGrid, FiList, FiSearch } from "react-icons/fi"
import { LuFilePlus } from "react-icons/lu";
import { z } from "zod"
import React from "react"

import { ActionsMenuWriting } from "@/components/Writings/ActionsMenuWriting"

import { WritingsService } from "@/client"
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot,
} from "@/components/ui/pagination"

import blank from "@/components/tiptap/templates/blank.json"
import resumeSerif from "@/components/tiptap/templates/resume-serif.json"
import resumeCoral from "@/components/tiptap/templates/resume-coral.json"
import letter from "@/components/tiptap/templates/letter.json"
import proposal from "@/components/tiptap/templates/proposal.json"
import report from "@/components/tiptap/templates/report.json"

const writingsSearchSchema = z.object({
  page: z.number().catch(1),
})

const PER_PAGE = 8

const templates = [
  { id: "blank", name: "빈 문서", icon: <LuFilePlus size="32px" color="#3182CE" />, content: blank },
  { id: "resume-serif", name: "공지사항", description: "2025-05-22", content: resumeSerif },
  { id: "resume-coral", name: "공문", description: "2025-05-21", content: resumeCoral },
  { id: "letter", name: "이메일 형식", description: "2025-04-18", content: letter },
  { id: "proposal", name: "실험보고서", description: "2025-03-08", content: proposal },
  { id: "report", name: "회의록", description: "2025-01-11", content: report }
]


function getWritingsQueryOptions({ page }: { page: number }) {
  return {
    queryFn: () =>
      WritingsService.readWritings({
        skip: (page - 1) * PER_PAGE,
        limit: PER_PAGE,
      }),
    queryKey: ["writings", { page }],
  }
}

export const Route = createFileRoute("/_layout/writings")({
  component: DocumentsDashboard,
  validateSearch: (search) => writingsSearchSchema.parse(search),
})


function TemplateGallery() {
  const navigate = useNavigate()

  const handleTemplateClick = async (templateName: string) => {
    const template = templates.find((t) => t.name === templateName)
    if (!template) return

    try {
      const res = await WritingsService.createWriting({
        requestBody: {
          title: templateName === "blank" ? "제목없음" : templateName,
          description: "",
          content: template.content,
        },
      })

      navigate({
        to: "/writing/$writingId",
        params: { writingId: res.id },
      })
    } catch (error) {
      console.error("❌ Failed to create writing from template:", error)
    }
  }

  return (
    <Box mb={12}>
      <Heading size="md" mb={4}>
        템플릿
      </Heading>
      <Grid
        templateColumns={{
          base: "repeat(2, 1fr)",
          sm: "repeat(3, 1fr)",
          md: "repeat(4, 1fr)",
          lg: "repeat(6, 1fr)",
        }}
        gap={4}
      >
        {templates.map((template) => (
          <VStack
            key={template.id}
            gap={1}
            align="start"
            borderWidth={1}
            borderRadius="md"
            p={3}
            cursor="pointer"
            onClick={() => handleTemplateClick(template.name)}
            _hover={{ shadow: "md", bg: "gray.50" }}
          >
            <Box
              w="100%"
              h="100px"
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              display="flex"
              justifyContent="center"
              alignItems="center"
              fontSize="2xl"
            >
              {template.icon || "📄"}
            </Box>
            <Text fontWeight="medium">{template.name}</Text>
            {template.description && (
              <Text fontSize="sm" color="gray.500">
                {template.description}
              </Text>
            )}
          </VStack>
        ))}
      </Grid>
    </Box>
  )
}


function WritingsGrid() {
  const navigate = useNavigate()
  const { page } = Route.useSearch()
  const { data, isLoading } = useQuery(getWritingsQueryOptions({ page }))
  const [viewMode, setViewMode] = React.useState<"grid" | "list">("grid")

  const setPage = (page: number) =>
    navigate({
      search: (prev: z.infer<typeof writingsSearchSchema>) => ({ ...prev, page }),
    })

  const writings = data?.data ?? []
  const count = data?.count ?? 0

  if (isLoading) return <VStack pt={10}>Loading...</VStack>

  if (writings.length === 0) {
    return (
      <VStack pt={10} gap={4} align="center">
        <FiSearch size={32} />
        <Text fontSize="lg">작성된 문서가 없습니다</Text>
        <Text fontSize="sm" color="gray.500">
          "빈 문서 추가하기" 버튼을 눌러 작성을 시작해보세요.
        </Text>
      </VStack>

    )
  }

  return (
    <>
      {/* View toggle buttons */}
      <Flex justifyContent="flex-end" mb={4}>
        <HStack gap={2}>
          <Button
            variant={viewMode === "grid" ? "solid" : "ghost"}
            size="sm"
            onClick={() => setViewMode("grid")}
          >
            <FiGrid />
          </Button>
          <Button
            variant={viewMode === "list" ? "solid" : "ghost"}
            size="sm"
            onClick={() => setViewMode("list")}
          >
            <FiList />
          </Button>
        </HStack>
      </Flex>

      {viewMode === "grid" ? (
        <Grid
          templateColumns={{
            base: "repeat(1, 1fr)",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(4, 1fr)",
          }}
          gap={6}
          mb={6}
        >
          {writings.map((writing) => (
            <Box
              key={writing.id}
              p={4}
              position="relative"
              borderWidth={1}
              borderRadius="md"
              boxShadow="sm"
              _hover={{ shadow: "md", cursor: "pointer" }}
              onClick={() =>
                navigate({ to: "/writing/$writingId", params: { writingId: writing.writing_id } })
              }
            >
              <Box position="absolute" top={2} right={2} zIndex={1} onClick={(e) => e.stopPropagation()}>
                {/* <ActionsMenu item={writing.id} /> */}
                <ActionsMenuWriting writingMetadata={writing} />
              </Box>

              <Text fontWeight="semibold" mb={1}>{writing.title}</Text>
              <Box maxH="2.5em" overflow="hidden">
                <Text fontSize="sm" color="gray.500">
                  {writing.description || "설명 없음"}
                </Text>
              </Box>
              <Text mt={2} fontSize="xs" color="gray.400">
                마지막 수정: {new Date(writing.content_updated_at).toLocaleDateString()}
              </Text>
            </Box>
          ))}
        </Grid>
      ) : (
        <Table.Root size={{ base: "sm", md: "md" }} mb={6}>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader w="5%">ID</Table.ColumnHeader>
              <Table.ColumnHeader w="30%">제목</Table.ColumnHeader>
              <Table.ColumnHeader w="45%">설명</Table.ColumnHeader>
              <Table.ColumnHeader w="20%">마지막 수정</Table.ColumnHeader>
              <Table.ColumnHeader w="20%">작업</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {writings.map((writing) => (
              <Table.Row
                key={writing.id}
                _hover={{ bg: "gray.50", cursor: "pointer" }}
                onClick={() =>
                  navigate({ to: "/writing/$writingId", params: { writingId: writing.writing_id } })
                }
              >
                <Table.Cell truncate maxW="5%">
                  {writing.id.split("-")[0]}
                </Table.Cell>
                <Table.Cell truncate maxW="30%">
                  {writing.title}
                </Table.Cell>
                <Table.Cell truncate maxW="45%" color={!writing.description ? "gray.400" : "inherit"}>
                  {writing.description || "설명 없음"}
                </Table.Cell>
                <Table.Cell truncate maxW="20%">
                  {new Date(writing.content_updated_at).toLocaleDateString()}
                </Table.Cell>
                <Table.Cell width="10%">
                  <Box onClick={(e) => e.stopPropagation()}>
                    <ActionsMenuWriting writingMetadata={writing} />
                  </Box>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}

      <Flex justifyContent="flex-end">
        <PaginationRoot
          count={count}
          pageSize={PER_PAGE}
          onPageChange={({ page }) => setPage(page)}
        >
          <Flex>
            <PaginationPrevTrigger />
            <PaginationItems />
            <PaginationNextTrigger />
          </Flex>
        </PaginationRoot>
      </Flex>
    </>
  )
}


function DocumentsDashboard() {

  return (
    <Container maxW="full" pt={8} overflowY="auto">
      <Flex justify="space-between" align="center" mb={8}>
        <Heading size="lg">AI 메모</Heading>
        {/* <Button
          colorScheme="blue"
          onClick={handleCreateWriting}
        >
          <FiPlus style={{ marginRight: '8px' }} />
          Add a new writing
        </Button> */}
      </Flex>
      <TemplateGallery />
      <WritingsGrid />
    </Container>
  )
}

export default DocumentsDashboard
